import { useState, type FormEvent } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { normalizeMobile, parseBackupFile } from '@curentia/shared';
import { ErrorText, Field, Page, Submit } from '../components';
import { DOORS, isDoor } from '../lib/doors';
import { useAction, useDevice, useSnapshot } from '../state';

export function Login() {
  const { door } = useParams();
  const device = useDevice();
  const { busy, error, setError, run } = useAction();
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  if (!isDoor(door)) return <Navigate to="/welcome" replace />;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const normalized = normalizeMobile(mobile);
    if (!normalized) return setError('Enter a valid Indian mobile number.');
    if (!password) return setError('Enter your password.');
    void run(() => device.logIn(door, normalized, password));
  };

  return (
    <Page>
      <h1>Sign in as {DOORS[door].label.toLowerCase()}</h1>
      <form onSubmit={submit} noValidate>
        <Field label="Mobile number" type="tel" inputMode="tel" autoComplete="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} required />
        <Field label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        <ErrorText error={error} />
        <Submit busy={busy} busyText="Signing in…">
          Sign in
        </Submit>
      </form>
      <div className="links">
        <Link to={`/signup/${door}`}>Create an account instead</Link>
        <Link to={`/start/${door}`}>Back</Link>
      </div>
    </Page>
  );
}

/** After signing in on a device with no Curentia data: restore a backup or start fresh. */
export function Setup() {
  const device = useDevice();
  const { account } = useSnapshot();
  const restore = useAction();
  const fresh = useAction();
  const [file, setFile] = useState<File | null>(null);
  const [olderPassword, setOlderPassword] = useState(false);
  const [code, setCode] = useState('');

  const submitRestore = (event: FormEvent) => {
    event.preventDefault();
    if (!file) return restore.setError('Choose your backup file.');
    if (olderPassword && !code.trim()) return restore.setError('Enter your recovery code.');
    void restore.run(async () => device.restoreAfterLogin(parseBackupFile(await file.text()), olderPassword ? code : undefined));
  };

  return (
    <Page>
      <h1>Set up this device</h1>
      <p>
        Welcome back{account ? `, ${account.name}` : ''}. This device has no Curentia data yet.
      </p>
      <section className="card">
        <h2>Restore from a backup file</h2>
        <p className="muted">Brings back your records and this account's keys from a backup you saved.</p>
        <form onSubmit={submitRestore} noValidate>
          <Field label="Backup file" type="file" accept="application/json,.json" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          <label className="check">
            <input type="checkbox" checked={olderPassword} onChange={(e) => setOlderPassword(e.target.checked)} />
            I made this backup before changing my password
          </label>
          {olderPassword ? <Field label="Recovery code" autoComplete="off" value={code} onChange={(e) => setCode(e.target.value)} /> : null}
          <ErrorText error={restore.error} />
          <Submit busy={restore.busy} busyText="Restoring…">
            Restore backup
          </Submit>
        </form>
      </section>
      <section className="card">
        <h2>Start fresh on this device</h2>
        <p className="muted">Records kept only on your other device will not appear here.</p>
        <ErrorText error={fresh.error} />
        <button type="button" className="button secondary" disabled={fresh.busy} onClick={() => void fresh.run(() => device.setUpNewDevice())}>
          {fresh.busy ? 'Setting up…' : 'Start fresh'}
        </button>
      </section>
      <div className="links">
        <button type="button" className="button secondary small" onClick={() => device.lock()}>
          Cancel
        </button>
      </div>
    </Page>
  );
}
